/**
 * BulkUnapproveDialog — step many selected assets back by one step at once
 *
 *
 * Bulk unapprove is the inverse of bulk approve: where approve advances each
 * asset one step, unapprove rewinds each asset one step by resetting its most
 * recently approved step (and every step after it), preserving approvals on
 * earlier steps. Because it's destructive — unlike bulk approve, which fires
 * immediately — this always confirms first. On success the per-asset results are
 * surfaced in a toast so the user sees how many were skipped (e.g. nothing
 * approved yet, or not a reviewer of the step being rewound). To step back more
 * than one step, repeat the action.
 *
 * Error UX is owned here (the mutation suppresses the global toast).
 */
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';

import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useBulkUnapprove } from '../../api/useBulkUnapprove.mutation';
import { summarizeBulkResults } from '../../lib/summarize-results';
import { useTranslation } from 'react-i18next';

export interface BulkUnapproveDialogProps {
  open: boolean;
  projectId: string;
  /** Folder in view — threaded to the mutation so the files list refreshes. */
  folderId?: string;
  /** Ids of the assets the user selected in the grid. */
  assetIds: string[];
  onClose: () => void;
  /** Called after a successful unapprove so the host can clear its selection. */
  onUnapproved?: () => void;
}

export function BulkUnapproveDialog({
  open,
  projectId,
  folderId,
  assetIds,
  onClose,
  onUnapproved,
}: BulkUnapproveDialogProps) {
  const { t } = useTranslation('projects');

  const bulkUnapprove = useBulkUnapprove({ projectId, folderId });

  const assetCount = assetIds.length;

  const handleClose = () => {
    if (bulkUnapprove.isPending) return; // don't close mid-request
    onClose();
  };

  const handleConfirm = () => {
    if (assetCount === 0) return;
    bulkUnapprove.mutate(assetIds, {
      onSuccess: (result) => {
        // The BE returns 200 with per-asset failures in the body, so a run where
        // every asset was skipped still resolves here. Only celebrate when at
        // least one asset actually changed; otherwise warn with the same summary.
        const summary = summarizeBulkResults(result.results, 'unapproved');
        if (result.results.some((r) => r.success)) {
          notificationService.showSuccess(summary);
        } else {
          notificationService.showWarning(summary);
        }
        onUnapproved?.();
        onClose();
      },
      onError: (error) => {
        const { detail } = parseProblemDetail(
          error,
          t('bulkApproveAssets.bulkUnapproveDialog.failedToUnapproveAssets'),
        );
        notificationService.showError(detail);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-120 gap-0 p-4" data-testid="bulk-unapprove-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('bulkApproveAssets.bulkUnapproveDialog.unapproveAssets', { count: assetCount })}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="pt-4">
          <p className="text-foreground-alt">
            {t('bulkApproveAssets.bulkUnapproveDialog.unapprovingStepsBack', {
              count: assetCount,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={bulkUnapprove.isPending}
          >
            {t('bulkApproveAssets.bulkUnapproveDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={assetCount === 0 || bulkUnapprove.isPending}
            data-testid="bulk-unapprove-confirm"
          >
            {bulkUnapprove.isPending && <Spinner className="size-4" />}
            {t('bulkApproveAssets.bulkUnapproveDialog.yesUnapprove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

BulkUnapproveDialog.displayName = 'BulkUnapproveDialog';
